// InsurerProfilePage.tsx - Insurer Claims Analyst Dashboard
import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface InsurerProfileData {
  insurer_names: string[];
  cost_overview: {
    total_spend: number;
    total_claims: number;
    avg_cost_per_claim: number;
    total_orders: number;
    avg_order_value: number;
  };
  cost_trend: { week: string; orders: number; spend: number; claims: number }[];
  claim_velocity: {
    total_claims: number;
    claims_pending_parts: number;
    avg_claim_days: number;
    closed_claims: number;
  };
  price_anomalies: {
    claim_number: string;
    part_name: string;
    part_type: string;
    supplier_name: string;
    price: number;
    avg_price: number;
    z_score: number;
    anomaly_type: string;
    variance: number;
  }[];
  supplier_performance: {
    supplier_name: string;
    total_orders: number;
    total_value: number;
    delivery_rate: number;
    cancel_rate: number;
    avg_days: number;
    risk_level: string;
  }[];
  problem_suppliers: {
    supplier_name: string;
    total_orders: number;
    cancel_rate: number;
    delivery_rate: number;
    cancelled_value: number;
  }[];
  savings_opportunities: {
    part_type: string;
    current_supplier: string;
    current_price: number;
    best_price: number;
    potential_savings: number;
    order_count: number;
    total_potential_savings: number;
  }[];
  workshop_analysis: {
    workshop_name: string;
    claim_count: number;
    order_count: number;
    total_cost: number;
    avg_cost_per_claim: number;
    avg_parts_per_claim: number;
  }[];
  workshop_outliers: {
    workshop_name: string;
    claims: number;
    avg_cost: number;
    market_avg: number;
    z_score: number;
  }[];
  duplicate_parts: {
    claim_number: string;
    part_name: string;
    duplicate_count: number;
    total_value: number;
    workshop_name: string;
  }[];
  high_cost_claims: {
    claim_number: string;
    total_cost: number;
    parts_count: number;
    workshop_name: string;
    z_score: number;
  }[];
  automation: {
    total_orders: number;
    auto_assigned: number;
    auto_quoted: number;
    auto_assign_rate: number;
    auto_quote_rate: number;
    manual_rate: number;
  };
  workshop_supplier_matrix: {
    workshop_name: string;
    supplier_name: string;
    order_count: number;
    total_value: number;
    pct_of_workshop_orders: number;
  }[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value);

const truncate = (str: string, len: number) =>
  str && str.length > len ? str.substring(0, len) + '...' : str;

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const riskColor = (level: string) => {
  if (level === 'HIGH') return 'bg-red-100 text-red-800';
  if (level === 'MEDIUM') return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
};

const InsurerProfilePage: React.FC<{ dateRange: DateRange; authFetch: (url: string, options?: RequestInit) => Promise<Response> }> = ({ dateRange, authFetch }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<InsurerProfileData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'anomalies' | 'suppliers' | 'workshops' | 'fraud' | 'savings'>('overview');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('startDate', dateRange.startDate);
        if (dateRange.endDate) params.append('endDate', dateRange.endDate);

        const res = await authFetch(`/api/insurer/profile?${params}`);
        const json = await res.json();

        if (json.success) {
          setData(json.data);
        } else {
          setError(json.error || 'Failed to load data');
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange, authFetch]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading insurer analytics...</div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded p-4 text-red-800">{error}</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data available</div>;

  const tabs = [
    { key: 'overview', label: 'Cost Overview' },
    { key: 'anomalies', label: 'Price Anomalies' },
    { key: 'suppliers', label: 'Supplier Performance' },
    { key: 'workshops', label: 'Workshop Analysis' },
    { key: 'fraud', label: 'Fraud Indicators' },
    { key: 'savings', label: 'Savings Opportunities' }
  ];

  const totalPotentialSavings = data.savings_opportunities.reduce((sum, s) => sum + s.total_potential_savings, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Claims Analytics Dashboard</h1>
            <p className="text-sm text-gray-500">{data.insurer_names.join(', ')}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{formatCurrency(data.cost_overview.total_spend)}</div>
            <div className="text-sm text-gray-500">Total Parts Spend</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{formatNumber(data.cost_overview.total_claims)}</div>
              <div className="text-sm text-gray-500">Total Claims</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{formatCurrency(data.cost_overview.avg_cost_per_claim)}</div>
              <div className="text-sm text-gray-500">Avg Cost/Claim</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-orange-600">{data.claim_velocity.claims_pending_parts}</div>
              <div className="text-sm text-gray-500">Claims Pending Parts</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{data.claim_velocity.avg_claim_days}</div>
              <div className="text-sm text-gray-500">Avg Days to Close</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-teal-600">{data.automation.auto_assign_rate}%</div>
              <div className="text-sm text-gray-500">Auto-Assign Rate</div>
            </div>
          </div>

          {/* Cost Trend */}
          {data.cost_trend.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Weekly Cost Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.cost_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.substring(5, 10)} />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="spend" fill="#3b82f6" name="Spend" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Automation Efficiency */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4">Automation Efficiency</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded">
                <div className="text-3xl font-bold text-green-600">{data.automation.auto_assign_rate}%</div>
                <div className="text-sm text-gray-500">Auto-Assign Rate</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded">
                <div className="text-3xl font-bold text-blue-600">{data.automation.auto_quote_rate}%</div>
                <div className="text-sm text-gray-500">Auto-Quote Rate</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded">
                <div className="text-3xl font-bold text-orange-600">{data.automation.manual_rate}%</div>
                <div className="text-sm text-gray-500">Manual Intervention</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Price Anomalies Tab */}
      {activeTab === 'anomalies' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Price Anomalies (Z-Score &gt; 2)</h3>
              <span className="text-sm text-gray-500">{data.price_anomalies.length} anomalies detected</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Claim</th>
                    <th className="px-3 py-2 text-left">Part</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Avg Price</th>
                    <th className="px-3 py-2 text-right">Variance</th>
                    <th className="px-3 py-2 text-right">Z-Score</th>
                    <th className="px-3 py-2 text-center">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.price_anomalies.map((a, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{truncate(a.claim_number, 12)}</td>
                      <td className="px-3 py-2">{truncate(a.part_name, 20)}</td>
                      <td className="px-3 py-2">{truncate(a.supplier_name, 15)}</td>
                      <td className="px-3 py-2 text-right font-bold">{formatCurrency(a.price)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{formatCurrency(a.avg_price)}</td>
                      <td className={`px-3 py-2 text-right ${a.variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {a.variance > 0 ? '+' : ''}{formatCurrency(a.variance)}
                      </td>
                      <td className="px-3 py-2 text-right font-bold">{a.z_score}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${a.anomaly_type === 'HIGH' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                          {a.anomaly_type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Suppliers Tab */}
      {activeTab === 'suppliers' && (
        <div className="space-y-6">
          {/* Supplier Performance */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Supplier Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Value</th>
                    <th className="px-4 py-2 text-right">Delivery %</th>
                    <th className="px-4 py-2 text-right">Cancel %</th>
                    <th className="px-4 py-2 text-right">Avg Days</th>
                    <th className="px-4 py-2 text-center">Risk</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.supplier_performance.map((s, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(s.supplier_name, 25)}</td>
                      <td className="px-4 py-2 text-right">{s.total_orders}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(s.total_value)}</td>
                      <td className="px-4 py-2 text-right text-green-600">{s.delivery_rate}%</td>
                      <td className="px-4 py-2 text-right text-red-600">{s.cancel_rate}%</td>
                      <td className="px-4 py-2 text-right">{s.avg_days}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${riskColor(s.risk_level)}`}>{s.risk_level}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Problem Suppliers */}
          {data.problem_suppliers.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-red-50">
                <h3 className="font-semibold text-red-800">Problem Suppliers (Cancel Rate &gt; 10%)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Supplier</th>
                      <th className="px-4 py-2 text-right">Orders</th>
                      <th className="px-4 py-2 text-right">Cancel Rate</th>
                      <th className="px-4 py-2 text-right">Cancelled Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.problem_suppliers.map((s, i) => (
                      <tr key={i} className="hover:bg-red-50">
                        <td className="px-4 py-2 font-medium">{truncate(s.supplier_name, 25)}</td>
                        <td className="px-4 py-2 text-right">{s.total_orders}</td>
                        <td className="px-4 py-2 text-right text-red-600 font-bold">{s.cancel_rate}%</td>
                        <td className="px-4 py-2 text-right text-red-600">{formatCurrency(s.cancelled_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Workshops Tab */}
      {activeTab === 'workshops' && (
        <div className="space-y-6">
          {/* Workshop Analysis */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Workshop Cost Analysis</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Workshop</th>
                    <th className="px-4 py-2 text-right">Claims</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Total Cost</th>
                    <th className="px-4 py-2 text-right">Avg Cost/Claim</th>
                    <th className="px-4 py-2 text-right">Avg Parts/Claim</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.workshop_analysis.map((w, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(w.workshop_name, 25)}</td>
                      <td className="px-4 py-2 text-right">{w.claim_count}</td>
                      <td className="px-4 py-2 text-right">{w.order_count}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(w.total_cost)}</td>
                      <td className="px-4 py-2 text-right font-bold">{formatCurrency(w.avg_cost_per_claim)}</td>
                      <td className="px-4 py-2 text-right">{w.avg_parts_per_claim}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Workshop Outliers */}
          {data.workshop_outliers.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-orange-50">
                <h3 className="font-semibold text-orange-800">High-Cost Workshop Outliers</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Workshop</th>
                      <th className="px-4 py-2 text-right">Claims</th>
                      <th className="px-4 py-2 text-right">Avg Cost</th>
                      <th className="px-4 py-2 text-right">Market Avg</th>
                      <th className="px-4 py-2 text-right">Z-Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.workshop_outliers.map((w, i) => (
                      <tr key={i} className="hover:bg-orange-50">
                        <td className="px-4 py-2 font-medium">{truncate(w.workshop_name, 25)}</td>
                        <td className="px-4 py-2 text-right">{w.claims}</td>
                        <td className="px-4 py-2 text-right text-red-600 font-bold">{formatCurrency(w.avg_cost)}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{formatCurrency(w.market_avg)}</td>
                        <td className="px-4 py-2 text-right">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded">{w.z_score}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Workshop-Supplier Matrix */}
          {data.workshop_supplier_matrix.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-purple-50">
                <h3 className="font-semibold text-purple-800">Workshop-Supplier Concentration (&gt;50% of orders)</h3>
                <p className="text-sm text-purple-600">Workshops sending majority of orders to single supplier</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Workshop</th>
                      <th className="px-4 py-2 text-left">Preferred Supplier</th>
                      <th className="px-4 py-2 text-right">Orders</th>
                      <th className="px-4 py-2 text-right">Value</th>
                      <th className="px-4 py-2 text-right">% of Workshop Orders</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.workshop_supplier_matrix.map((m, i) => (
                      <tr key={i} className="hover:bg-purple-50">
                        <td className="px-4 py-2 font-medium">{truncate(m.workshop_name, 20)}</td>
                        <td className="px-4 py-2">{truncate(m.supplier_name, 20)}</td>
                        <td className="px-4 py-2 text-right">{m.order_count}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(m.total_value)}</td>
                        <td className="px-4 py-2 text-right">
                          <span className={`px-2 py-1 rounded text-xs ${m.pct_of_workshop_orders > 80 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                            {m.pct_of_workshop_orders}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fraud Indicators Tab */}
      {activeTab === 'fraud' && (
        <div className="space-y-6">
          {/* Duplicate Parts */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b bg-red-50">
              <h3 className="font-semibold text-red-800">Duplicate Parts on Claims</h3>
            </div>
            {data.duplicate_parts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Claim</th>
                      <th className="px-4 py-2 text-left">Part</th>
                      <th className="px-4 py-2 text-left">Workshop</th>
                      <th className="px-4 py-2 text-center">Duplicates</th>
                      <th className="px-4 py-2 text-right">Total Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.duplicate_parts.map((d, i) => (
                      <tr key={i} className="hover:bg-red-50">
                        <td className="px-4 py-2 font-medium">{truncate(d.claim_number, 15)}</td>
                        <td className="px-4 py-2">{truncate(d.part_name, 25)}</td>
                        <td className="px-4 py-2">{truncate(d.workshop_name, 20)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-bold">{d.duplicate_count}</span>
                        </td>
                        <td className="px-4 py-2 text-right">{formatCurrency(d.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">No duplicate parts detected</div>
            )}
          </div>

          {/* High Cost Claims */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b bg-orange-50">
              <h3 className="font-semibold text-orange-800">High-Cost Claim Outliers (Z-Score &gt; 2)</h3>
            </div>
            {data.high_cost_claims.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Claim</th>
                      <th className="px-4 py-2 text-left">Workshop</th>
                      <th className="px-4 py-2 text-right">Parts</th>
                      <th className="px-4 py-2 text-right">Total Cost</th>
                      <th className="px-4 py-2 text-right">Z-Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.high_cost_claims.map((c, i) => (
                      <tr key={i} className="hover:bg-orange-50">
                        <td className="px-4 py-2 font-medium">{truncate(c.claim_number, 15)}</td>
                        <td className="px-4 py-2">{truncate(c.workshop_name, 20)}</td>
                        <td className="px-4 py-2 text-right">{c.parts_count}</td>
                        <td className="px-4 py-2 text-right text-red-600 font-bold">{formatCurrency(c.total_cost)}</td>
                        <td className="px-4 py-2 text-right">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded">{c.z_score}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">No high-cost outliers detected</div>
            )}
          </div>
        </div>
      )}

      {/* Savings Tab */}
      {activeTab === 'savings' && (
        <div className="space-y-6">
          {/* Savings Summary */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">{formatCurrency(totalPotentialSavings)}</div>
              <div className="text-sm text-gray-500">Total Potential Savings Identified</div>
            </div>
          </div>

          {/* Savings Opportunities */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Savings Opportunities by Part Type</h3>
              <p className="text-sm text-gray-500">Suppliers charging &gt;10% above best available price</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Part Type</th>
                    <th className="px-4 py-2 text-left">Current Supplier</th>
                    <th className="px-4 py-2 text-right">Current Price</th>
                    <th className="px-4 py-2 text-right">Best Price</th>
                    <th className="px-4 py-2 text-right">Savings/Unit</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Total Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.savings_opportunities.map((s, i) => (
                    <tr key={i} className="hover:bg-green-50">
                      <td className="px-4 py-2 font-medium">{truncate(s.part_type, 20)}</td>
                      <td className="px-4 py-2">{truncate(s.current_supplier, 20)}</td>
                      <td className="px-4 py-2 text-right text-red-600">{formatCurrency(s.current_price)}</td>
                      <td className="px-4 py-2 text-right text-green-600">{formatCurrency(s.best_price)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(s.potential_savings)}</td>
                      <td className="px-4 py-2 text-right">{s.order_count}</td>
                      <td className="px-4 py-2 text-right font-bold text-green-600">{formatCurrency(s.total_potential_savings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsurerProfilePage;